import React from "react";
import { Joyride } from "react-joyride";
import { useTourContext } from "./TourProvider";
import { tourStyles } from "./tourTheme";
import { useTranslation } from "react-i18next";
import { useRTL } from "src/Hooks/useRTL";

function TourTooltip({
  backProps,
  continuous,
  index,
  isLastStep,
  primaryProps,
  skipProps,
  step,
  tooltipProps,
  t,
}) {
  return (
    <div
      {...tooltipProps}
      className="w-[min(360px,calc(100vw-32px))] max-w-[calc(100vw-32px)] rounded-[12px] border border-[var(--bgLightGray)] p-2 sm:p-4"
      style={{
        ...(tooltipProps.style || {}),
        backgroundColor: "var(--white)",
        color: "var(--black)",
      }}
    >
      {step.title ? (
        <div className="mb-2 text-[15px] font-semibold text-[var(--lessDark)]">
          {step.title}
        </div>
      ) : null}
      <div className="text-[14px] leading-6 text-[var(--black)]">
        {step.content}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <button
          {...skipProps}
          type="button"
          className="rounded-[8px] px-2 py-1 text-[13px] font-medium text-black transition-colors hover:text-green-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-300"
        >
          {t("Tour_SkipAllSteps")}
        </button>
        <div className="flex items-center gap-2">
          {index > 0 ? (
            <button
              {...backProps}
              type="button"
              className="rounded-[8px] px-3 py-2 text-[13px] font-medium text-black transition-colors hover:bg-green-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-300"
            >
              {t("Tour_Back")}
            </button>
          ) : null}
          {continuous ? (
            <button
              {...primaryProps}
              type="button"
              className="rounded-[8px] bg-purple px-4 py-2 text-[13px] font-semibold text-black transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-300"
            >
              {isLastStep ? t("Tour_Finish") : t("Tour_Next")}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function TourRunner() {
  const { run, runId, tourSteps, handleJoyrideCallback } = useTourContext();
  const { t } = useTranslation();
  const isRTL = useRTL();

  if (!tourSteps.length) return null;

  const CustomBeacon = (props) => {
    const { ...restProps } = props;

    return (
      <button
        {...restProps}
        className="relative inline-flex items-center justify-center cursor-pointer border-0 rounded-full"
        style={{
          width: 24,
          height: 24,
          backgroundColor: "var(--purple)",
          boxShadow: "0 0 0 4px rgba(var(--purple-rgb, 111, 56, 197), 0.25)",
          animation: "tour-beacon-pulse 2s ease-in-out infinite",
        }}
        aria-label="Open tour"
      >
        <span
          className="absolute rounded-full"
          style={{
            width: 10,
            height: 10,
            backgroundColor: "var(--onAccent)",
          }}
        />
        <span
          className="absolute rounded-full"
          style={{
            inset: -8,
              border: "2px solid var(--purple)",
            opacity: 0,
            animation: "tour-beacon-ripple 2s ease-out infinite",
          }}
        />
        <style>{`
          @keyframes tour-beacon-pulse {
            0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(var(--purple-rgb, 111, 56, 197), 0.4); }
            50% { transform: scale(1.08); box-shadow: 0 0 0 8px rgba(var(--purple-rgb, 111, 56, 197), 0); }
          }
          @keyframes tour-beacon-ripple {
            0% { transform: scale(1); opacity: 0.5; }
            100% { transform: scale(2.2); opacity: 0; }
          }
        `}</style>
      </button>
    );
  };

  return (
    <Joyride
      key={runId}
      steps={tourSteps}
      run={run}
      continuous
      showProgress
      scrollOffset={350}
      overlayClickAction={false}
      loaderComponent={null}
      targetWaitTimeout={0}
      beaconComponent={CustomBeacon}
      floaterProps={{
        offset: 20,
        strategy: "fixed",
        styles: {
          floater: {
            filter: "drop-shadow(0 4px 24px var(--bgBlack))",
            clipPath: "none",
          },
          arrow: {
            color: "var(--white)",
            borderColor: "var(--bgLightGray)",
          },
        },
        modifiers: [
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
              padding: 60,
              escapeWithReference: true,
            },
          },
          {
            name: "offset",
            options: {
              offset: [0, 20],
            },
          },
          {
            name: "flip",
            options: {
              padding: 60,
            },
          },
        ],
      }}
      locale={{
        back: t("Tour_Back"),
        next: t("Tour_Next"),
        skip: t("Tour_SkipAllSteps"),
        last: t("Tour_Finish"),
        close: t("Tour_Close"),
      }}
      styles={tourStyles(isRTL)}
      tooltipComponent={(props) => <TourTooltip {...props} t={t} />}
      callback={handleJoyrideCallback}
    />
  );
}
